if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        this.buildModalTitle(productElement);
      }

      /* liftsets (22 Sep 2026). Dawn hides the product h1 inside this modal and expects a second,
         linked copy of the title to take its place. On this theme that copy was display:none in every
         context, because .ls-title-row .product__title a.product__title in assets/liftsets-pdp.css
         outranks the rule in assets/quick-add.css that tries to show it, so the modal has never shown
         a product name. The duplicate was deleted from the product page on 22 Sep for the heading
         outline, which is correct for the page and left the modal with no name at all.

         So the linked title is built HERE, from the fetched h1, instead of being carried in the
         product page's markup. The product page keeps exactly one title; the modal gets a visible,
         clickable heading; and because the h1 is replaced rather than hidden, a collection page does
         not end up with a second h1 in its document while the modal is open. It is rebuilt on every
         preprocess, so a variant re-render inside the modal keeps it. */
      buildModalTitle(productElement) {
        const wrapper = productElement.querySelector('.product__title');
        if (!wrapper || wrapper.querySelector('a.product__title')) return;
        const heading = wrapper.querySelector('h1');
        const name = heading?.textContent.trim();
        if (!name) return;
        const href =
          productElement.dataset.url ||
          productElement.querySelector('.product__view-details')?.getAttribute('href');
        const link = document.createElement('a');
        link.className = 'product__title ls-quick-add-title';
        if (href) link.href = href;
        const title = document.createElement('h2');
        title.className = 'h1';
        title.textContent = name;
        link.appendChild(title);
        heading.replaceWith(link);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        /* liftsets (BUILD-xlix): the fetched product page carries its own "Often bought with" cards inside
           product-info, each with a <quick-add-modal>; connected inside this modal (outside any .shopify-section)
           their connectedCallback threw "Cannot read properties of null (reading 'id')". Drop the block and any
           nested quick-add modals — a quick-add inside a quick-add is never shown (.quick-add-hidden). */
        productElement.querySelectorAll('[data-ls-obw], quick-add-modal').forEach((el) => el.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
